import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, Minus, Truck, MapPin, Package } from "lucide-react";
import api, { money, assetUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ConditionBadge } from "@/components/ConditionBadge";
import { HeartButton } from "@/components/HeartButton";
import { Seo } from "@/components/Seo";
import { ProductReviews } from "@/components/ProductReviews";
import { toast } from "sonner";
import { trackViewContent, trackAddToCart } from "@/lib/fbPixel";

export default function ProductDetail() {
  const { id } = useParams();
  const { add } = useCart();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [qty, setQty] = useState(1);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => { setProduct(r.data); trackViewContent(r.data); }).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="max-w-[1400px] mx-auto px-8 py-24 text-center font-mono">Product not found. <Link to="/" className="text-link underline">Back to catalog</Link></div>;
  if (!product) return <div className="max-w-[1400px] mx-auto px-8 py-24 text-center font-mono text-subtle">Loading…</div>;

  const soldOut = (product.available ?? product.quantity) <= 0;
  const stock = product.available ?? product.quantity;
  const videos = product.videos || [];
  const imgs = product.images && product.images.length ? product.images : [];
  const hasVideo = videos.length > 0;
  const activeStill = imgs.length ? imgs[Math.min(activeImg, imgs.length - 1)] : "";

  const condMap = { "New / Brand New / In Box (NIB)": "https://schema.org/NewCondition", "Like New / Mint Condition": "https://schema.org/UsedCondition", "New Other / Scratch and Dent": "https://schema.org/RefurbishedCondition", "Floor Model / Display": "https://schema.org/UsedCondition", "Good / Fair Condition / Tested & Working": "https://schema.org/UsedCondition", "Used / Pre-owned / Tested & Working": "https://schema.org/UsedCondition", "As-Is / Untested / Salvage / For Parts": "https://schema.org/UsedCondition" };
  const productLd = {
    "@context": "https://schema.org", "@type": "Product",
    name: product.name,
    description: (product.details || product.description || product.condition_notes || `${product.condition} — surplus/liquidation item for short-term rental operators.`).slice(0, 400),
    sku: product.sku || product.id, ...(product.upc ? { gtin: product.upc } : {}),
    ...(product.brand ? { brand: { "@type": "Brand", name: product.brand } } : {}),
    ...(imgs.length ? { image: imgs.map((i) => (i.startsWith("http") ? i : `https://strsurplus.com${i}`)) } : {}),
    category: product.category,
    offers: {
      "@type": "Offer", url: `https://strsurplus.com/product/${product.id}`,
      priceCurrency: "USD", price: Number(product.our_price || 0).toFixed(2),
      itemCondition: condMap[product.condition] || "https://schema.org/UsedCondition",
      availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      seller: { "@type": "Organization", name: "STR Surplus" },
    },
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-8 py-8">
      <Seo
        title={product.name}
        description={`${product.name} — ${product.condition}. ${money(product.our_price)}${product.list_price > product.our_price ? ` (was ${money(product.list_price)})` : ""}. Surplus/liquidation stock for short-term rental owners & operators. Local pickup in Fort Lauderdale or UPS Ground shipping.`}
        path={`/product/${product.id}`}
        image={imgs[0]}
        jsonLd={productLd}
      />
      <Link to="/" data-testid="back-to-catalog" className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-subtle hover:text-ink transition-colors mb-6">
        <ArrowLeft className="h-4 w-4" /> Back to catalog
      </Link>

      <div className="grid lg:grid-cols-2 gap-8">
        {/* MEDIA */}
        <div>
          {hasVideo ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="aspect-square border-2 border-ink bg-surface overflow-hidden">
                {activeStill ? (
                  <img src={assetUrl(activeStill)} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
                ) : (
                  <div className="h-full w-full flex items-center justify-center text-subtle"><Package className="h-8 w-8" /></div>
                )}
                <span className="sr-only">Product photo</span>
              </div>
              <div className="aspect-square border-2 border-ink bg-ink overflow-hidden">
                <video src={assetUrl(videos[0])} className="h-full w-full object-cover" autoPlay loop muted playsInline controls data-testid="product-main-video" />
              </div>
            </div>
          ) : (
            <div className="aspect-square border-2 border-ink bg-surface overflow-hidden">
              {activeStill ? (
                <img src={assetUrl(activeStill)} alt={product.name} className="h-full w-full object-cover" data-testid="product-main-image" />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-subtle"><Package className="h-8 w-8" /></div>
              )}
            </div>
          )}
          {imgs.length > 1 && (
            <div className="flex gap-2 mt-3 flex-wrap">
              {imgs.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`h-20 w-20 border-2 overflow-hidden ${i === activeImg ? "border-link" : "border-surface2"}`}>
                  <img src={assetUrl(im)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* INFO */}
        <div>
          <div className="font-mono text-[11px] text-subtle uppercase tracking-wider">{product.sku || "—"} · {product.brand ? product.brand + " · " : ""}{product.category}{product.lot_name ? ` · ${product.lot_name}` : ""}</div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tighter mt-2 leading-none">{product.name}</h1>

          <div className="mt-4 flex items-center gap-3">
            <ConditionBadge condition={product.condition} />
            {product.discount_pct > 0 && (
              <span className="bg-discount text-ink font-heading font-black text-sm px-2 py-1 border border-ink">-{product.discount_pct}% OFF LIST</span>
            )}
            <HeartButton product={product} className="ml-auto h-9 px-3 gap-2 border border-ink hover:bg-danger hover:text-white font-mono text-[11px] uppercase tracking-wider before:content-['Alert_Me']" />
          </div>

          <div className="mt-6 flex items-end gap-4 border-y-2 border-ink py-4">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-widest text-subtle">Our Price</div>
              <div className="font-heading font-black text-5xl tracking-tighter leading-none">{money(product.our_price)}</div>
            </div>
            {product.list_price > 0 && (
              <div className="pb-1">
                <div className="font-mono text-[11px] uppercase tracking-widest text-subtle">List</div>
                <div className="font-mono text-xl text-subtle line-through">{money(product.list_price)}</div>
              </div>
            )}
          </div>

          {product.description && <p className="font-mono text-sm text-subtle mt-4 leading-relaxed">{product.description}</p>}

          {product.details && product.specs_status !== "ai_draft" && (
            <div className="mt-6 border-2 border-ink" data-testid="detail-specs">
              <div className="bg-ink text-paper font-mono text-[11px] uppercase tracking-widest px-4 py-2">Product Details / Specs</div>
              <p className="font-mono text-sm leading-relaxed whitespace-pre-wrap p-4">{product.details}</p>
            </div>
          )}

          {product.condition_notes && (
            <div className="mt-4 bg-surface border-l-4 border-danger p-4">
              <div className="font-mono text-[11px] uppercase tracking-widest text-danger font-bold mb-1">Condition Notes</div>
              <p className="font-mono text-sm">{product.condition_notes}</p>
            </div>
          )}

          <div className="mt-6 font-mono text-xs uppercase tracking-wider" style={{ color: soldOut ? "#FF3B30" : stock <= 3 ? "#FF3B30" : "#00C566" }}>
            {soldOut ? "● Out of stock" : `● ${stock} units available`}
          </div>

          <div className="mt-6 flex items-center gap-4">
            <div className="flex items-center border-2 border-ink h-12">
              <button data-testid="detail-qty-dec" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 h-full hover:bg-ink hover:text-paper transition-colors"><Minus className="h-4 w-4" /></button>
              <span className="px-4 font-mono">{qty}</span>
              <button data-testid="detail-qty-inc" onClick={() => setQty((q) => Math.min(stock, q + 1))} className="px-3 h-full hover:bg-ink hover:text-paper transition-colors"><Plus className="h-4 w-4" /></button>
            </div>
            <button
              data-testid="detail-add-to-cart"
              disabled={soldOut}
              onClick={() => { add(product, qty); trackAddToCart(product, qty); toast.success(`${qty} × ${product.name} added`); }}
              className="flex-1 bg-ink text-paper h-12 font-mono text-sm uppercase tracking-[0.15em] shadow-hard hover:translate-x-1 hover:translate-y-1 hover:shadow-none transition-transform disabled:opacity-30"
            >
              Add To Cart · {money(product.our_price * qty)}
            </button>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-6 font-mono text-[11px] uppercase tracking-wider">
            <div className="border border-ink p-3 flex flex-col gap-1"><MapPin className="h-4 w-4 text-success" /> Free local pickup</div>
            <div className="border border-ink p-3 flex flex-col gap-1"><Truck className="h-4 w-4 text-link" /> UPS Ground avail.</div>
            <div className="border border-ink p-3 flex flex-col gap-1"><Package className="h-4 w-4" /> {product.weight_lbs} lbs</div>
          </div>
        </div>
      </div>

      <ProductReviews productId={product.id} />
    </div>
  );
}
